import React from 'react';
import { Box, Checkbox, FormControlLabel, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import { PRES, SPACE, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PresentationSettingsState, SettingsImportStyle, SettingsTextStyle } from '../presentationSettings';
import { ALLOWED_FONTS } from '../types';
import { AdminLabeledControl } from './AdminInfoLabel';
import AdminColorSelect from './AdminColorSelect';
import AdminSettingCard from './AdminSettingCard';
import ChartDefaultsPreview from './ChartDefaultsPreview';
import TableDefaultsPreview from './TableDefaultsPreview';
import { adminFieldSx } from './adminFormSx';

interface AdminTextTableSectionProps {
  draft: PresentationSettingsState;
  setDraft: React.Dispatch<React.SetStateAction<PresentationSettingsState>>;
}

function StyleFields({
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  textTransform,
  onPatch,
  showLineHeight,
}: {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  lineHeight?: number;
  textTransform: 'none' | 'uppercase';
  onPatch: (patch: Partial<SettingsTextStyle & SettingsImportStyle>) => void;
  showLineHeight?: boolean;
}) {
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: showLineHeight ? '1.4fr 90px 90px 90px auto' : '1.4fr 90px 90px auto' },
      gap: 1.5,
      alignItems: 'end',
    }}
    >
      <AdminLabeledControl label={COPY.settingsFontFamily}>
        {({ id }) => (
          <Select
            id={id}
            size="small"
            fullWidth
            value={fontFamily}
            onChange={(event) => onPatch({ fontFamily: event.target.value })}
            sx={adminFieldSx()}
          >
            {ALLOWED_FONTS.map((font) => <MenuItem key={font} value={font}>{font}</MenuItem>)}
          </Select>
        )}
      </AdminLabeledControl>
      <AdminLabeledControl label={COPY.settingsFontSize}>
        {({ id }) => (
          <TextField
            id={id}
            size="small"
            type="number"
            value={fontSize}
            onChange={(event) => onPatch({ fontSize: Number(event.target.value) })}
            InputProps={{ endAdornment: <InputAdornment position="end">{COPY.settingsUnitPt}</InputAdornment> }}
            sx={adminFieldSx()}
          />
        )}
      </AdminLabeledControl>
      <AdminLabeledControl label={COPY.settingsFontWeight}>
        {({ id }) => (
          <TextField
            id={id}
            size="small"
            type="number"
            value={fontWeight}
            onChange={(event) => onPatch({ fontWeight: Number(event.target.value) })}
            sx={adminFieldSx()}
          />
        )}
      </AdminLabeledControl>
      {showLineHeight && lineHeight != null && (
        <AdminLabeledControl label={COPY.settingsLineHeight}>
          {({ id }) => (
            <TextField
              id={id}
              size="small"
              type="number"
              value={lineHeight}
              onChange={(event) => onPatch({ lineHeight: Number(event.target.value) })}
              inputProps={{ step: 0.05 }}
              sx={adminFieldSx()}
            />
          )}
        </AdminLabeledControl>
      )}
      <FormControlLabel
        control={(
          <Checkbox
            checked={textTransform === 'uppercase'}
            onChange={(event) => onPatch({ textTransform: event.target.checked ? 'uppercase' : 'none' })}
          />
        )}
        label={<Typography sx={{ fontFamily: TYPE.ui, ...TYPE.label }}>{COPY.settingsUppercase}</Typography>}
      />
    </Box>
  );
}

const AdminTextTableSection: React.FC<AdminTextTableSectionProps> = ({ draft, setDraft }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SPACE[5]}px`, maxWidth: 920 }}>
    <AdminSettingCard title={COPY.settingsNewTextCard}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <AdminLabeledControl label={COPY.settingsNewTextPlaceholder} hint={COPY.settingsNewTextPlaceholderHelp}>
          {({ id, describedBy }) => (
            <TextField
              id={id}
              size="small"
              fullWidth
              value={draft.textStyles.newTextPlaceholder}
              onChange={(event) => setDraft((current) => ({
                ...current,
                textStyles: { ...current.textStyles, newTextPlaceholder: event.target.value },
              }))}
              inputProps={{ 'aria-describedby': describedBy }}
              sx={{ ...adminFieldSx(), maxWidth: 520 }}
            />
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsNewTextStyle} hint={COPY.settingsNewTextStyleHelp}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              size="small"
              fullWidth
              value={draft.textStyles.newTextStyleId}
              onChange={(event) => setDraft((current) => ({
                ...current,
                textStyles: { ...current.textStyles, newTextStyleId: event.target.value as SettingsTextStyle['id'] },
              }))}
              inputProps={{ 'aria-describedby': describedBy }}
              sx={{ ...adminFieldSx(), maxWidth: 520 }}
            >
              {draft.textStyles.steps.map((step) => (
                <MenuItem key={step.id} value={step.id}>{step.label}</MenuItem>
              ))}
            </Select>
          )}
        </AdminLabeledControl>
      </Box>
    </AdminSettingCard>

    <AdminSettingCard title={COPY.settingsEditorStylesCard} description={COPY.settingsEditorStylesHelp}>
      {draft.textStyles.steps.map((step, index) => (
        <Box key={step.id} sx={{ mb: index === draft.textStyles.steps.length - 1 ? 0 : 3 }}>
          <AdminLabeledControl label={COPY.settingsColorName}>
            {({ id }) => (
              <TextField
                id={id}
                size="small"
                value={step.label}
                onChange={(event) => {
                  const steps = draft.textStyles.steps.map((item, i) => (
                    i === index ? { ...item, label: event.target.value } : item
                  ));
                  setDraft((current) => ({ ...current, textStyles: { ...current.textStyles, steps } }));
                }}
                sx={{ ...adminFieldSx(), maxWidth: 280, mb: 1.5 }}
              />
            )}
          </AdminLabeledControl>
          <StyleFields
            fontFamily={step.fontFamily}
            fontSize={step.fontSize}
            fontWeight={step.fontWeight}
            lineHeight={step.lineHeight}
            textTransform={step.textTransform}
            showLineHeight
            onPatch={(patch) => {
              const steps = draft.textStyles.steps.map((item, i) => (i === index ? { ...item, ...patch } : item));
              setDraft((current) => ({ ...current, textStyles: { ...current.textStyles, steps } }));
            }}
          />
        </Box>
      ))}
    </AdminSettingCard>

    <AdminSettingCard title={COPY.settingsImportCard} description={COPY.settingsImportHelp}>
      {(['importHeading', 'importBody'] as const).map((key) => {
        const style = draft.textStyles[key];
        const label = key === 'importHeading' ? COPY.settingsImportHeading : COPY.settingsImportBody;
        return (
          <Box key={key} sx={{ mb: key === 'importBody' ? 0 : 3 }}>
            <Typography sx={{ mb: 1.25, color: PRES.textPrimary, fontFamily: TYPE.ui, ...TYPE.label, fontWeight: 650 }}>
              {label}
            </Typography>
            <StyleFields
              fontFamily={style.fontFamily}
              fontSize={style.fontSize}
              fontWeight={style.fontWeight}
              textTransform={style.textTransform}
              onPatch={(patch) => setDraft((current) => ({
                ...current,
                textStyles: { ...current.textStyles, [key]: { ...style, ...patch } },
              }))}
            />
          </Box>
        );
      })}
    </AdminSettingCard>

    <AdminSettingCard title={COPY.settingsTableCard} description={COPY.settingsTableHelp}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, minmax(140px, 1fr))' },
        gap: 2,
        mb: 2,
      }}
      >
        <AdminLabeledControl label={COPY.settingsTableCols} hint={COPY.settingsTableColsHelp}>
          {({ id, describedBy }) => (
            <TextField
              id={id}
              size="small"
              type="number"
              fullWidth
              value={draft.tableDefaults.cols}
              onChange={(event) => setDraft((current) => ({
                ...current,
                tableDefaults: { ...current.tableDefaults, cols: Number(event.target.value) },
              }))}
              inputProps={{ min: 1, max: 12, 'aria-describedby': describedBy }}
              sx={adminFieldSx()}
            />
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsTableRows} hint={COPY.settingsTableRowsHelp}>
          {({ id, describedBy }) => (
            <TextField
              id={id}
              size="small"
              type="number"
              fullWidth
              value={draft.tableDefaults.rows}
              onChange={(event) => setDraft((current) => ({
                ...current,
                tableDefaults: { ...current.tableDefaults, rows: Number(event.target.value) },
              }))}
              inputProps={{ min: 1, max: 40, 'aria-describedby': describedBy }}
              sx={adminFieldSx()}
            />
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsTableFont} hint={COPY.settingsTableFontHelp}>
          {({ id, describedBy }) => (
            <TextField
              id={id}
              size="small"
              type="number"
              fullWidth
              value={draft.tableDefaults.fontSize}
              onChange={(event) => setDraft((current) => ({
                ...current,
                tableDefaults: { ...current.tableDefaults, fontSize: Number(event.target.value) },
              }))}
              inputProps={{ min: 8, max: 72, 'aria-describedby': describedBy }}
              InputProps={{ endAdornment: <InputAdornment position="end">{COPY.settingsUnitPt}</InputAdornment> }}
              sx={adminFieldSx()}
            />
          )}
        </AdminLabeledControl>
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, minmax(160px, 1fr))' },
        gap: 2,
        mb: 2.5,
      }}
      >
        <AdminLabeledControl label={COPY.settingsTableHeaderColor} hint={COPY.settingsTableHeaderHelp}>
          {({ id, describedBy }) => (
            <AdminColorSelect
              id={id}
              describedBy={describedBy}
              value={draft.tableDefaults.headerBgColorId}
              colors={draft.colors}
              onChange={(value) => setDraft((current) => ({
                ...current,
                tableDefaults: { ...current.tableDefaults, headerBgColorId: value },
              }))}
              fullWidth
            />
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsTableGridColor} hint={COPY.settingsTableGridHelp}>
          {({ id, describedBy }) => (
            <AdminColorSelect
              id={id}
              describedBy={describedBy}
              value={draft.tableDefaults.gridColorId}
              colors={draft.colors}
              onChange={(value) => setDraft((current) => ({
                ...current,
                tableDefaults: { ...current.tableDefaults, gridColorId: value },
              }))}
              fullWidth
            />
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsTableZebraColor} hint={COPY.settingsTableZebraHelp}>
          {({ id, describedBy }) => (
            <AdminColorSelect
              id={id}
              describedBy={describedBy}
              value={draft.tableDefaults.zebraColorId}
              colors={draft.colors}
              onChange={(value) => setDraft((current) => ({
                ...current,
                tableDefaults: { ...current.tableDefaults, zebraColorId: value },
              }))}
              fullWidth
            />
          )}
        </AdminLabeledControl>
      </Box>
      <TableDefaultsPreview settings={draft} />
    </AdminSettingCard>

    <AdminSettingCard title={COPY.settingsChartCard} description={COPY.settingsChartHelp}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, minmax(140px, 1fr))' },
        gap: 2,
        mb: 2,
      }}
      >
        <AdminLabeledControl label={COPY.settingsChartKind} hint={COPY.settingsChartKindHelp}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              size="small"
              fullWidth
              value={draft.chartDefaults.kind}
              onChange={(event) => setDraft((current) => ({
                ...current,
                chartDefaults: {
                  ...current.chartDefaults,
                  kind: event.target.value as PresentationSettingsState['chartDefaults']['kind'],
                },
              }))}
              inputProps={{ 'aria-describedby': describedBy }}
              sx={adminFieldSx()}
            >
              <MenuItem value="column">{COPY.chartColumn}</MenuItem>
              <MenuItem value="bar">{COPY.chartBar}</MenuItem>
              <MenuItem value="line">{COPY.chartLine}</MenuItem>
              <MenuItem value="pie">{COPY.chartPie}</MenuItem>
            </Select>
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsChartFont} hint={COPY.settingsChartFontHelp}>
          {({ id, describedBy }) => (
            <TextField
              id={id}
              size="small"
              type="number"
              fullWidth
              value={draft.chartDefaults.fontSize}
              onChange={(event) => setDraft((current) => ({
                ...current,
                chartDefaults: { ...current.chartDefaults, fontSize: Number(event.target.value) },
              }))}
              inputProps={{ min: 8, max: 72, 'aria-describedby': describedBy }}
              InputProps={{ endAdornment: <InputAdornment position="end">{COPY.settingsUnitPt}</InputAdornment> }}
              sx={adminFieldSx()}
            />
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsChartBackground} hint={COPY.settingsChartBackgroundHelp}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              size="small"
              fullWidth
              value={draft.chartDefaults.background || 'none'}
              onChange={(event) => setDraft((current) => ({
                ...current,
                chartDefaults: {
                  ...current.chartDefaults,
                  background: event.target.value as PresentationSettingsState['chartDefaults']['background'],
                },
              }))}
              inputProps={{ 'aria-describedby': describedBy }}
              sx={adminFieldSx()}
            >
              <MenuItem value="none">{COPY.chartBackgroundNone}</MenuItem>
              <MenuItem value="white">{COPY.chartBackgroundWhite}</MenuItem>
              <MenuItem value="sand">{COPY.chartBackgroundSand}</MenuItem>
            </Select>
          )}
        </AdminLabeledControl>
        <AdminLabeledControl label={COPY.settingsChartGridColor} hint={COPY.settingsChartGridHelp}>
          {({ id, describedBy }) => (
            <AdminColorSelect
              id={id}
              describedBy={describedBy}
              value={draft.chartDefaults.gridColorId}
              colors={draft.colors}
              onChange={(value) => setDraft((current) => ({
                ...current,
                chartDefaults: { ...current.chartDefaults, gridColorId: value },
              }))}
              fullWidth
            />
          )}
        </AdminLabeledControl>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={draft.chartDefaults.showLegend}
              onChange={(event) => setDraft((current) => ({
                ...current,
                chartDefaults: { ...current.chartDefaults, showLegend: event.target.checked },
              }))}
            />
          )}
          label={COPY.settingsChartLegend}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={draft.chartDefaults.showValues}
              onChange={(event) => setDraft((current) => ({
                ...current,
                chartDefaults: { ...current.chartDefaults, showValues: event.target.checked },
              }))}
            />
          )}
          label={COPY.settingsChartValues}
        />
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, minmax(160px, 1fr))' },
        gap: 2,
        mb: 2.5,
      }}
      >
        {draft.chartDefaults.seriesColorIds.map((colorId, index) => (
          <AdminLabeledControl
            key={`chart-series-${index}`}
            label={`${COPY.settingsChartSeriesColor} ${index + 1}`}
            hint={COPY.settingsChartSeriesHelp}
          >
            {({ id, describedBy }) => (
              <AdminColorSelect
                id={id}
                describedBy={describedBy}
                value={colorId}
                colors={draft.colors}
                onChange={(value) => setDraft((current) => ({
                  ...current,
                  chartDefaults: {
                    ...current.chartDefaults,
                    seriesColorIds: current.chartDefaults.seriesColorIds.map((item, itemIndex) => (
                      itemIndex === index ? value : item
                    )),
                  },
                }))}
                fullWidth
              />
            )}
          </AdminLabeledControl>
        ))}
      </Box>
      <ChartDefaultsPreview settings={draft} />
    </AdminSettingCard>
  </Box>
);

export default AdminTextTableSection;
